import type { ColorSystemOptions, PaletteOptions } from '@mui/material/styles';

// Shared between schemes: brand colours do not change with the colour scheme.
const common = {
  white: '#FFFFFF',
  primary: {
    main: '#A08C64',
    dark: '#897858',
  },
  secondary: {
    main: '#FF824B',
  },
  warnings: {
    minor: '#FFAE57',
    moderate: '#FF824B',
    major: '#FF2E2E',
  },
  // Dims content behind overlays; stays dark in both schemes.
  scrim: '#212121',
};

export const lightPalette = {
  ...common,
  grey: {
    '50': '#FAFAFA',
    '100': '#F5F5F5',
    '200': '#EEEEEE',
    '300': '#E0E0E0',
    '400': '#BDBDBD',
    '500': '#9E9E9E',
    '600': '#757575',
    '700': '#616161',
    '800': '#424242',
    '900': '#212121',
  },
  tertiary: {
    main: '#EEEEEE',
  },
  success: {
    main: '#129B4E',
    light: '#E1FAEC',
  },
  info: {
    main: '#327DFF',
    light: '#C8DCFF',
  },
  error: {
    main: '#FF2E2E',
    light: '#FAE1E1',
  },
  inverted: {
    main: '#212121',
    contrastText: '#FFFFFF',
  },
  surface: {
    input: '#F5F5F5',
    inputHover: '#EEEEEE',
    raised: '#FFFFFF',
  },
  greyAlpha: {
    10: 'rgba(0, 0, 0, 0.02)',
    20: 'rgba(0, 0, 0, 0.06)',
    30: 'rgba(0, 0, 0, 0.09)',
    40: 'rgba(0, 0, 0, 0.15)',
  },
} satisfies PaletteOptions;

// Dark values follow SAM (`sam/packages/theme/src/themes/g/darkTheme.ts`) so
// both products look alike. The `.light` status variants are background tints,
// so they become dark tints here.
export const darkPalette = {
  ...common,
  // Inverted so `grey[50..300]` stay subtle surfaces/borders and
  // `grey[700..900]` stay high-contrast text, as they are in light mode.
  grey: {
    '50': '#2A2A2A',
    '100': '#303030',
    '200': '#3A3A3A',
    '300': '#434343',
    '400': '#4E4E4E',
    '500': '#757575',
    '600': '#9E9E9E',
    '700': '#BDBDBD',
    '800': '#E0E0E0',
    '900': '#F5F5F5',
  },
  background: {
    default: '#242424',
    paper: '#272727',
  },
  // MUI derives these dark defaults from `grey`, which is inverted above,
  // so pin them to the values MUI would use with its standard greys.
  AppBar: { defaultBg: '#212121' },
  Avatar: { defaultBg: '#757575' },
  Button: {
    inheritContainedBg: '#424242',
    inheritContainedHoverBg: '#616161',
  },
  Chip: {
    defaultBorder: '#616161',
    defaultAvatarColor: '#E0E0E0',
    defaultIconColor: '#E0E0E0',
  },
  StepConnector: { border: '#757575' },
  StepContent: { border: '#757575' },
  Switch: {
    defaultColor: '#E0E0E0',
    defaultDisabledColor: '#757575',
  },
  Tooltip: { bg: 'rgba(97, 97, 97, 0.92)' },
  tertiary: {
    main: '#3A3A3A',
  },
  success: {
    main: '#129B4E',
    light: '#032E15',
  },
  info: {
    main: '#327DFF',
    light: '#001C4A',
  },
  error: {
    main: '#FF2E2E',
    light: '#450000',
  },
  inverted: {
    main: '#F5F5F5',
    contrastText: '#212121',
  },
  surface: {
    input: 'rgba(255, 255, 255, 0.09)',
    inputHover: 'rgba(255, 255, 255, 0.13)',
    raised: '#303030',
  },
  greyAlpha: {
    10: 'rgba(255, 255, 255, 0.04)',
    20: 'rgba(255, 255, 255, 0.09)',
    30: 'rgba(255, 255, 255, 0.13)',
    40: 'rgba(255, 255, 255, 0.18)',
  },
} satisfies ColorSystemOptions['palette'];
